import { findActiveNavItem } from '@dittolive/anvil'
import { useQuery } from '@tanstack/react-query'
import graphql from 'babel-plugin-relay/macro'
import { AnnouncementsContainer } from 'components/announcement'
import { SystemCollections } from 'components/dittoAppConnection'
import useRpcClient from 'components/hooks/rpc/useRpcClient'
import { PortalShell } from 'components/layout'
import DatabaseActionMenu from 'components/navigationBar/DatabaseActionMenu'
import { usePrefetchQuery } from 'components/relay'
import { useApplyTenant } from 'components/tenant'
import AppContext from 'context'
import paths from 'paths'
import { Suspense, useContext, useEffect } from 'react'
import {
  PreloadedQuery,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { useLocation } from 'react-router-dom'
import { usePortalMutation } from 'utils'
import { useFeatureFlags } from 'utils/featureFlags'

import { AppManagementSceneQuery } from './__generated__/AppManagementSceneQuery.graphql'
import { AppManagementSceneVisitMutation } from './__generated__/AppManagementSceneVisitMutation.graphql'
import AppManagementContextProvider from './AppManagementContextProvider'
import AppManagementRouter from './AppManagementRouter'
import { useAppNavSections } from './appNavSections'
import { useAppURLParams, useCurrentAppArea } from './hooks'
import { AppAccessRouterQuery } from './tabs/access'
import { AppOfflineLicenseTokenSectionQuery } from './tabs/access/license/__generated__/AppOfflineLicenseTokenSectionQuery.graphql'
import {
  isOfflineLicenseAvailable,
  query as licenseQuery,
} from './tabs/access/license/AppOfflineLicenseTokenSection'
import { AppConnectHttpsSceneQuery } from './tabs/auth'
import {
  AppDangerSectionQuery,
  AppSettingsFormQuery,
  TransferAppSectionQuery,
} from './tabs/settings'

export const query = graphql`
  query AppManagementSceneQuery($appId: ID!) {
    viewer {
      appById(id: $appId) {
        # eslint-disable-next-line relay/unused-fields
        name
        organizationSlug
        # eslint-disable-next-line relay/unused-fields
        tenant {
          __typename
          ... on Organization {
            name
          }
        }
        cloudUrls {
          url
          wsEnabled
          httpEnabled
        }
        hydraCluster {
          isDedicated
          isValet
        }
        # eslint-disable-next-line relay/unused-fields
        announcements {
          id
          title
          summary
          details
          severity
        }
        viewerCanAccessMetrics
        viewerCanReadLicense
        viewerCanReadApiKey
        viewerCanReadAppData
        viewerCanWriteAppData
        viewerCanReadStreams
        viewerCanUpdate
        viewerCanReadMongoConnector
      }
    }
  }
`

const visitMutation = graphql`
  mutation AppManagementSceneVisitMutation($appId: ID!) {
    visitApp(appId: $appId) {
      id
    }
  }
`

type ContainerProps = {
  /** Current app ID. */
  appId: string
  /** True if the domain status is ready since the first moment the status is queried. */
  isDomainInitiallyReady: boolean
  /** True if the ingress domain for the app is ready. */
  isDomainReady: boolean
  /** True if the ingress domain for the app is in error state. */
  isDomainInError: boolean
  /** Number of times the domain status has been queried. */
  queryAttempts: number
}

/** Scene for managing apps */
export default function AppManagementSceneContainer(props: ContainerProps) {
  const [queryRef, loadQuery] = useQueryLoader<AppManagementSceneQuery>(query)

  const { appId } = props
  useEffect(() => {
    loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
  }, [appId, loadQuery])

  if (!queryRef) {
    return null
  }

  return <AppManagementScene {...props} queryRef={queryRef} />
}

type Props = {
  queryRef: PreloadedQuery<AppManagementSceneQuery>
} & ContainerProps

function AppManagementScene({ queryRef, ...props }: Props) {
  const { viewer } = usePreloadedQuery<AppManagementSceneQuery>(query, queryRef)
  if (!viewer.appById?.viewerCanReadLicense) {
    return (
      <AppManagementSceneContent
        {...props}
        queryRef={queryRef}
        allowLicenseTokens={false}
      />
    )
  }
  return <AppManagementSceneWithLicenses {...props} queryRef={queryRef} />
}

function AppManagementSceneWithLicenses(props: Props) {
  const { isSuperAdmin } = useContext(AppContext)
  const { viewer } = useLazyLoadQuery<AppOfflineLicenseTokenSectionQuery>(
    licenseQuery,
    { appId: props.appId },
  )
  return (
    <AppManagementSceneContent
      {...props}
      allowLicenseTokens={isOfflineLicenseAvailable(
        viewer.appById,
        !!isSuperAdmin,
      )}
    />
  )
}

function AppManagementSceneContent({
  queryRef,
  appId,
  isDomainInitiallyReady,
  isDomainReady,
  isDomainInError,
  queryAttempts,
  allowLicenseTokens,
}: Props & { allowLicenseTokens: boolean }) {
  const params = useAppURLParams()
  const { isSuperAdmin } = useContext(AppContext)
  const {
    viewer: { appById: app },
  } = usePreloadedQuery<AppManagementSceneQuery>(query, queryRef)
  const { metrics, unifiedConnectionUrl } = useFeatureFlags()
  const cloudDomain = app?.cloudUrls?.[0]
  const cloudDomainUrl = cloudDomain?.url
  const wsEnabled = cloudDomain?.wsEnabled
  const httpEnabled = cloudDomain?.httpEnabled
  const { executeDqlQuery, rpcAccessToken } = useRpcClient(
    appId,
    isDomainReady ? cloudDomainUrl : undefined,
  )
  const { data: sinksData } = useQuery({
    queryKey: ['hasConfiguredStream', appId],
    queryFn: () =>
      executeDqlQuery(
        `SELECT * FROM ${SystemCollections.LIVE_QUERY_SINKS_COLLECTION} LIMIT 1`,
      ),
    enabled: !!rpcAccessToken,
  })

  const viewerCanUpdateApp = app?.viewerCanUpdate ?? false
  const viewerCanReadAppData = app?.viewerCanReadAppData ?? false
  const viewerCanWriteAppData = app?.viewerCanWriteAppData ?? false
  const viewerCanReadStreams = app?.viewerCanReadStreams ?? false
  const viewerCanReadLicense = app?.viewerCanReadLicense ?? false
  const viewerCanReadApiKey = app?.viewerCanReadApiKey ?? false
  const isDedicatedCluster = !!app?.hydraCluster?.isDedicated
  const isValet = !!app?.hydraCluster?.isValet

  const viewerCanReadMongoConnector = !!app?.viewerCanReadMongoConnector
  const viewerCanAccessMongo =
    viewerCanReadMongoConnector && isValet && isDedicatedCluster

  const hasConfiguredStream = !!sinksData?.items?.length
  const streamsEnabled = isDedicatedCluster || hasConfiguredStream
  const allowStreams = streamsEnabled && viewerCanReadStreams

  const allowMetrics =
    !!(isSuperAdmin || app?.viewerCanAccessMetrics) && !!metrics
  useApplyTenant(params.organizationSlug ?? null)
  const { prefetchQueries } = usePrefetchQuery()

  const [markAppAsVisited] = usePortalMutation<AppManagementSceneVisitMutation>(
    visitMutation,
    {
      mutationName: 'VisitApp',
      sentryContext: {
        component: 'AppManagementScene',
        appId,
      },
    },
  )

  useEffect(() => {
    if (app) {
      markAppAsVisited({
        variables: { appId },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app])

  // Staff-override data gates. Derived once here so the nav and every router
  // share one policy (via AppManagementContext) instead of re-deriving it and
  // drifting. Super admins override the per-permission checks.
  const isStaff = !!isSuperAdmin
  const allowCollections = viewerCanReadAppData || isStaff
  const allowDevices = viewerCanReadAppData || isStaff
  const atLeastOneAuthSection =
    allowLicenseTokens || viewerCanReadApiKey || viewerCanReadAppData
  const allowAuth = atLeastOneAuthSection || isStaff
  const allowAccess = atLeastOneAuthSection || isStaff
  const allowAuthentication = viewerCanReadAppData || isStaff
  const defaultArea = allowCollections
    ? ('collections' as const)
    : atLeastOneAuthSection
      ? ('connect/sdk' as const)
      : ('settings' as const)

  const navSections = useAppNavSections({
    organizationSlug: params.organizationSlug,
    appSlug: params.appSlug,
    userId: params.userId,
    showAuth: allowAuth,
    showHttpsConnect: !unifiedConnectionUrl,
    isDomainReady,
    showCollections: allowCollections,
    showDevices: allowDevices,
    showAuthentication: allowAuthentication,
    showApiKeys: viewerCanReadApiKey,
    showLicenseTokens: allowLicenseTokens,
    allowMetrics,
    allowStreams,
    showSuperAdminSettings: !!isSuperAdmin,
    showMongoConnector: viewerCanAccessMongo,
    onPrefetchAuth: () =>
      prefetchQueries([AppConnectHttpsSceneQuery, AppAccessRouterQuery], {
        appId,
      }),
    onPrefetchSettings: () =>
      prefetchQueries(
        [AppSettingsFormQuery, AppDangerSectionQuery, TransferAppSectionQuery],
        { appId },
      ),
  })

  const location = useLocation()

  const backTo = params.organizationSlug
    ? paths.organizationManagement({
        slug: params.organizationSlug,
        area: 'databases',
      })
    : paths.userApps({ userId: params.userId })

  const activeItem = findActiveNavItem(navSections, location.pathname)
  const pageName = activeItem?.breadcrumbText ?? activeItem?.text

  // Pages under a nav section flagged `showInBreadcrumb` get a section
  // breadcrumb segment, e.g. My App / Access / Authentication.
  const activeSection = navSections.find((section) =>
    section.items.some((item) => item === activeItem),
  )
  const sectionBreadcrumb = activeSection?.showInBreadcrumb
    ? activeSection.label
    : undefined

  const tenantName =
    app?.tenant?.__typename === 'Organization' ? app.tenant.name : 'Personal'

  const currentArea = useCurrentAppArea()

  const getAppUrl = (appSlug: string, orgSlug?: string) =>
    paths.appManagement({
      organizationSlug: orgSlug,
      appSlug,
      tenantId: params.userId,
      area: currentArea ?? defaultArea,
    })

  const newDatabaseUrl = params.organizationSlug
    ? paths.createOrganizationApp(params.organizationSlug)
    : paths.createApp({ userId: params.userId })

  const breadcrumbs = [
    {
      text: app?.name ?? params.appSlug,
      render: () =>
        // In the admin context (/u/:userId/:appSlug) the switcher would list
        // the viewer's own apps but navigate within the target user's tenant,
        // producing broken URLs, so render a static label instead.
        params.userId ? (
          <span className="text-foreground-subtle text-sm">
            {app?.name ?? params.appSlug ?? ''}
          </span>
        ) : (
          <DatabaseActionMenu
            currentAppSlug={params.appSlug ?? ''}
            currentAppName={app?.name ?? params.appSlug ?? ''}
            organizationSlug={params.organizationSlug}
            getAppUrl={getAppUrl}
            allDatabasesUrl={backTo}
            newDatabaseUrl={newDatabaseUrl}
          />
        ),
    },
    ...(sectionBreadcrumb ? [{ text: sectionBreadcrumb }] : []),
    ...(pageName ? [{ text: pageName }] : []),
  ]

  return (
    <AppManagementContextProvider
      appId={appId}
      cloudDomainUrl={cloudDomainUrl}
      wsEnabled={wsEnabled ?? undefined}
      httpEnabled={httpEnabled ?? undefined}
      isDomainInitiallyReady={isDomainInitiallyReady}
      isDomainReady={isDomainReady}
      isDomainInError={isDomainInError}
      queryAttempts={queryAttempts}
      isDedicatedCluster={isDedicatedCluster}
      allowStreams={allowStreams}
      viewerCanAccessMetrics={allowMetrics}
      viewerCanReadAppData={viewerCanReadAppData}
      viewerCanWriteAppData={viewerCanWriteAppData}
      viewerCanReadLicense={viewerCanReadLicense}
      allowLicenseTokens={allowLicenseTokens}
      viewerCanReadApiKey={viewerCanReadApiKey}
      allowCollections={allowCollections}
      allowDevices={allowDevices}
      allowAuth={allowAuth}
      allowAccess={allowAccess}
      allowAuthentication={allowAuthentication}
      defaultArea={defaultArea}
      viewerCanUpdateApp={viewerCanUpdateApp}
      viewerCanAccessMongo={viewerCanAccessMongo}
    >
      <PortalShell
        navSections={navSections}
        backTo={backTo}
        backLabel="Databases"
        breadcrumbs={breadcrumbs}
        showTenantBreadcrumb
        tenantBreadcrumbName={tenantName}
      >
        <AnnouncementsContainer announcements={app?.announcements} />
        <Suspense fallback={null}>
          <AppManagementRouter appId={appId} />
        </Suspense>
      </PortalShell>
    </AppManagementContextProvider>
  )
}
